import React, { useState, useEffect } from 'react';
import {
  Phone,
  PhoneCall,
  Plus,
  Play,
  Pause,
  Search,
  Filter,
  Download,
  Upload,
  Calendar,
  Users,
  TrendingUp,
  BarChart3,
  Clock,
  CheckCircle,
  XCircle,
  PhoneOff,
  RefreshCw,
  Edit,
  Trash2,
  Eye,
  MoreVertical,
  Tag,
  Settings
} from 'lucide-react';
import { salesAPI, leadCategoriesAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import clsx from 'clsx';

// Pipeline options for outbound calls
const PIPELINE_OPTIONS = [
  { value: 'retell', label: 'Retell AI (Recommended)', description: 'Full-service AI calling with voicemail detection' },
  { value: 'layer1', label: 'Layer 1 (OpenAI Realtime)', description: 'Low latency, OpenAI voices' },
  { value: 'layer2', label: 'Layer 2 (Claude + Cartesia)', description: 'Claude AI with Cartesia TTS' },
  { value: 'layer3', label: 'Layer 3 (Claude + ElevenLabs)', description: 'Claude AI with ElevenLabs voices' },
  { value: 'layer4', label: 'Layer 4 (Realtime + ElevenLabs)', description: 'Best transcription + ethnic voices' }
];

// Voice options organized by pipeline
const VOICE_OPTIONS_BY_PIPELINE = {
  retell: [
    { value: '11labs-Adrian', label: 'Adrian (Default Male)' },
    { value: '11labs-Dorothy', label: 'Dorothy (Female)' },
    { value: '11labs-Drew', label: 'Drew (Male)' },
    { value: '11labs-Paul', label: 'Paul (Male)' },
    { value: '11labs-Marissa', label: 'Marissa (Female)' },
    { value: '11labs-Myra', label: 'Myra (Female)' },
    { value: '11labs-Ryan', label: 'Ryan (Male)' },
    { value: '11labs-Sarah', label: 'Sarah (Female)' }
  ],
  layer1: [
    { value: 'alloy', label: 'Alloy' },
    { value: 'ash', label: 'Ash' },
    { value: 'ballad', label: 'Ballad' },
    { value: 'coral', label: 'Coral' },
    { value: 'echo', label: 'Echo' },
    { value: 'sage', label: 'Sage' },
    { value: 'shimmer', label: 'Shimmer' },
    { value: 'verse', label: 'Verse' }
  ],
  layer2: [
    { value: 'a0e99841-438c-4a64-b679-ae501e7d6091', label: 'Barbershop Man' },
    { value: '79a125e8-cd45-4c13-8a67-188112f4dd22', label: 'British Lady' },
    { value: '95856005-0332-41b0-935f-352e296aa0df', label: 'Classy British Man' },
    { value: 'ee7ea9f8-c0c1-498c-9f62-dc2627e1c6ec', label: 'Commercial Lady' },
    { value: 'c2ac25f9-ecc4-4f56-9095-651354df60c0', label: 'Commercial Man' },
    { value: '41534e16-2966-4c6b-9670-111411def906', label: 'Confident Woman' },
    { value: '5345cf08-6f37-424d-a5d9-8ae1571e1d1a', label: 'Helpful Woman' },
    { value: '638efaaa-4d0c-442e-b701-3fae16aad012', label: 'Indian Lady' }
  ],
  layer3: [
    { value: 'YjlcD3XHztjJEo2wNszv', label: 'Tyre G (African American Male)' },
    { value: '9wYX8b0wRvLUEYtGuzP5', label: 'KeKe (Black Woman)' },
    { value: 'Ogc84JIyxBeL4VxfwZTd', label: 'Elijah May' }
  ],
  layer4: [
    { value: '9wYX8b0wRvLUEYtGuzP5', label: 'KeKe (Black Woman)' },
    { value: 'MEiBY6lrwud0dREUa5FQ', label: 'Brian (African American Radio Jockey)' },
    { value: 'YjlcD3XHztjJEo2wNszv', label: 'Tyre G (African American Male)' },
    { value: 'CwhRBWXzGAHq8TQ4Fs17', label: 'Roger (Laid-Back Male)' },
    { value: 'EXAVITQu4vr4xnSDxMaL', label: 'Sarah (Mature Female)' },
    { value: 'cgSgspJ2msm6clMCkdW9', label: 'Jessica (Playful Female)' },
    { value: 'Ogc84JIyxBeL4VxfwZTd', label: 'Elijah May' }
  ]
};

// Default voice for each pipeline
const DEFAULT_VOICE_BY_PIPELINE = {
  retell: '11labs-Adrian',
  layer1: 'coral',
  layer2: 'a0e99841-438c-4a64-b679-ae501e7d6091',
  layer3: 'YjlcD3XHztjJEo2wNszv',
  layer4: '9wYX8b0wRvLUEYtGuzP5'
};

const SalesCampaignsPage = () => {
  const [activeTab, setActiveTab] = useState('campaigns'); // campaigns, leads, analytics, categories
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  // Lead Categories state
  const [categories, setCategories] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    color: '#6B7280',
    priority: 0,
    useMainAiConfig: true,
    aiPersonality: '',
    aiTone: 'friendly',
    aiInstructions: '',
    aiContext: '',
    aiObjectives: []
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [importing, setImporting] = useState(false);

  // Add Lead form state
  const [leadForm, setLeadForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    title: ''
  });

  // Campaign form state
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    description: '',
    objective: 'qualification',
    introMessage: '',
    qualifyingQuestion: '',
    questions: ['', ''],
    closingMessage: '',
    pipeline: 'layer1',
    voiceModel: 'coral',
    fromPhoneNumber: '',
    callProvider: 'retell', // 'retell' (Retell AI) or 'vonage' (OpenAI Realtime)
    maxAttemptsPerLead: 3,
    retryDelayHours: 24,
    callingHoursStart: '09:00',
    callingHoursEnd: '17:00',
    timezone: 'America/Chicago',
    schedulingMode: 'window', // 'window' or 'specific'
    specificCallTimes: [''], // Array of HH:MM times
    // AI Configuration
    useMainAiConfig: true,
    aiPersonality: '',
    aiTone: 'friendly',
    aiInstructions: '',
    aiContext: ''
  });

  // Import leads modal state (showImportModal declared above on line 122)
  const [importSource, setImportSource] = useState('leadgen');
  const [importFilters, setImportFilters] = useState({});
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Handle pipeline change - update voice to default for new pipeline
  const handlePipelineChange = (newPipeline) => {
    setCampaignForm(prev => ({
      ...prev,
      pipeline: newPipeline,
      voiceModel: DEFAULT_VOICE_BY_PIPELINE[newPipeline]
    }));
  };

  useEffect(() => {
    loadCampaigns();
    loadCategories();
  }, []);

  useEffect(() => {
    if (selectedCampaign) {
      loadLeads(selectedCampaign.id);
    }
  }, [selectedCampaign]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const response = await salesAPI.getCampaigns({ status: 'all' });
      setCampaigns(response.data.data.campaigns || []);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await leadCategoriesAPI.getCategories();
      setCategories(response.data.data.categories || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      if (categoryForm.id) {
        await leadCategoriesAPI.updateCategory(categoryForm.id, categoryForm);
      } else {
        await leadCategoriesAPI.createCategory(categoryForm);
      }
      setShowCategoryModal(false);
      resetCategoryForm();
      loadCategories();
    } catch (error) {
      console.error('Failed to save category:', error);
      alert('Failed to save category: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEditCategory = (category) => {
    setCategoryForm({
      id: category.id,
      name: category.name,
      description: category.description || '',
      color: category.color || '#6B7280',
      priority: category.priority || 0,
      useMainAiConfig: category.useMainAiConfig !== false,
      aiPersonality: category.aiPersonality || '',
      aiTone: category.aiTone || 'friendly',
      aiInstructions: category.aiInstructions || '',
      aiContext: category.aiContext || '',
      aiObjectives: category.aiObjectives || []
    });
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm('Are you sure you want to delete this category?')) return;
    try {
      await leadCategoriesAPI.deleteCategory(categoryId);
      loadCategories();
    } catch (error) {
      console.error('Failed to delete category:', error);
      alert('Failed to delete category');
    }
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      name: '',
      description: '',
      color: '#6B7280',
      priority: 0,
      useMainAiConfig: true,
      aiPersonality: '',
      aiTone: 'friendly',
      aiInstructions: '',
      aiContext: '',
      aiObjectives: []
    });
  };

  const loadLeads = async (campaignId) => {
    try {
      setLoading(true);
      const response = await salesAPI.getLeads(campaignId, { limit: 100 });
      setLeads(response.data.data.leads || []);
    } catch (error) {
      console.error('Failed to load leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    try {
      // Build questions array: qualifying question + additional questions
      const allQuestions = [
        campaignForm.qualifyingQuestion,
        ...campaignForm.questions
      ].filter(q => q && q.trim());

      // Filter out empty call times
      const filteredCallTimes = campaignForm.specificCallTimes.filter(t => t && t.trim());

      // Destructure to exclude fields that shouldn't be sent to API
      // Backend gets tenantId from req.user (auth middleware)
      const { id, tenantId, qualifyingQuestion, questions, specificCallTimes, ...campaignData } = campaignForm;

      const payload = {
        ...campaignData,
        questions: allQuestions,
        specificCallTimes: filteredCallTimes
      };

      // If ID exists, we're updating; otherwise, creating
      if (id) {
        await salesAPI.updateCampaign(id, payload);
      } else {
        await salesAPI.createCampaign(payload);
      }

      setShowCreateModal(false);
      resetCampaignForm();
      loadCampaigns();
    } catch (error) {
      console.error(`Failed to ${campaignForm.id ? 'update' : 'create'} campaign:`, error);
      alert(`Failed to ${campaignForm.id ? 'update' : 'create'} campaign: ` + (error.response?.data?.error || error.message));
    }
  };

  const handleStartCampaign = async (campaignId) => {
    try {
      await salesAPI.startCampaign(campaignId);
      loadCampaigns();
    } catch (error) {
      console.error('Failed to start campaign:', error);
      alert('Failed to start campaign');
    }
  };

  const handlePauseCampaign = async (campaignId) => {
    try {
      await salesAPI.pauseCampaign(campaignId);
      loadCampaigns();
    } catch (error) {
      console.error('Failed to pause campaign:', error);
      alert('Failed to pause campaign');
    }
  };

  const handleEditCampaign = (campaign) => {
    // Parse the questions back into the form format
    const questions = campaign.questions || [];
    const qualifyingQuestion = questions[0] || '';
    const additionalQuestions = questions.slice(1);

    // Ensure we have at least 2 question slots
    while (additionalQuestions.length < 2) {
      additionalQuestions.push('');
    }

    // Parse specific call times
    let specificTimes = campaign.specificCallTimes || [];
    if (!Array.isArray(specificTimes)) specificTimes = [];
    if (specificTimes.length === 0) specificTimes = [''];

    setCampaignForm({
      id: campaign.id, // Add ID to track which campaign we're editing
      name: campaign.name,
      description: campaign.description || '',
      objective: campaign.objective || 'qualification',
      introMessage: campaign.introMessage || '',
      qualifyingQuestion,
      questions: additionalQuestions,
      closingMessage: campaign.closingMessage || '',
      pipeline: campaign.pipeline || 'layer1',
      voiceModel: campaign.voiceModel || 'coral',
      fromPhoneNumber: campaign.fromPhoneNumber || '',
      maxAttemptsPerLead: campaign.maxAttemptsPerLead || 3,
      retryDelayHours: campaign.retryDelayHours || 24,
      callingHoursStart: campaign.callingHoursStart || '09:00',
      callingHoursEnd: campaign.callingHoursEnd || '17:00',
      timezone: campaign.timezone || 'America/Chicago',
      schedulingMode: campaign.schedulingMode || 'window',
      specificCallTimes: specificTimes,
      // AI Configuration
      useMainAiConfig: campaign.useMainAiConfig !== false,
      aiPersonality: campaign.aiPersonality || '',
      aiTone: campaign.aiTone || 'friendly',
      aiInstructions: campaign.aiInstructions || '',
      aiContext: campaign.aiContext || ''
    });

    setShowCreateModal(true);
  };

  const resetCampaignForm = () => {
    setCampaignForm({
      name: '',
      description: '',
      objective: 'qualification',
      introMessage: '',
      qualifyingQuestion: '',
      questions: ['', ''],
      closingMessage: '',
      pipeline: 'layer1',
      voiceModel: 'coral',
      fromPhoneNumber: '',
      callProvider: 'retell',
      maxAttemptsPerLead: 3,
      retryDelayHours: 24,
      callingHoursStart: '09:00',
      callingHoursEnd: '17:00',
      timezone: 'America/Chicago',
      schedulingMode: 'window',
      specificCallTimes: [''],
      // AI Configuration
      useMainAiConfig: true,
      aiPersonality: '',
      aiTone: 'friendly',
      aiInstructions: '',
      aiContext: ''
    });
  };

  // Handle import leads from external source
  const handleImportLeads = async () => {
    if (!selectedCampaign) {
      alert('Please select a campaign first');
      return;
    }

    setImportLoading(true);
    setImportResult(null);

    try {
      const response = await salesAPI.importExternalLeads({
        campaignId: selectedCampaign.id,
        source: importSource,
        filters: importFilters
      });

      setImportResult(response.data.data);

      // Refresh leads list
      if (selectedCampaign) {
        const leadsResponse = await salesAPI.getCampaignLeads(selectedCampaign.id);
        setLeads(leadsResponse.data.data?.leads || []);
      }

      // Refresh campaigns to update counts
      await loadCampaigns();
    } catch (error) {
      console.error('Failed to import leads:', error);
      setImportResult({
        error: error.response?.data?.error || error.message || 'Failed to import leads'
      });
    } finally {
      setImportLoading(false);
    }
  };

  // Helper functions for specific call times
  const addCallTime = () => {
    setCampaignForm({
      ...campaignForm,
      specificCallTimes: [...campaignForm.specificCallTimes, '']
    });
  };

  const removeCallTime = (index) => {
    const newTimes = campaignForm.specificCallTimes.filter((_, i) => i !== index);
    setCampaignForm({
      ...campaignForm,
      specificCallTimes: newTimes.length > 0 ? newTimes : ['']
    });
  };

  const updateCallTime = (index, value) => {
    const newTimes = [...campaignForm.specificCallTimes];
    newTimes[index] = value;
    setCampaignForm({
      ...campaignForm,
      specificCallTimes: newTimes
    });
  };

  const resetLeadForm = () => {
    setLeadForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      title: ''
    });
  };

  const handleAddLead = async (e) => {
    e.preventDefault();
    try {
      await salesAPI.createLead(selectedCampaign.id, leadForm);
      setShowAddLeadModal(false);
      resetLeadForm();
      loadLeads(selectedCampaign.id);
    } catch (error) {
      console.error('Failed to add lead:', error);
      alert('Failed to add lead: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleImportCSV = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      alert('Please select a CSV file');
      return;
    }

    try {
      setImporting(true);
      const response = await salesAPI.importLeads(selectedCampaign.id, csvFile);
      const { imported, attempted, errors } = response.data.data;

      let message = `Successfully imported ${imported} out of ${attempted} leads`;
      if (errors && errors.length > 0) {
        message += `\n\nErrors: ${errors.length} rows had issues`;
      }

      alert(message);
      setShowImportModal(false);
      setCsvFile(null);
      loadLeads(selectedCampaign.id);
    } catch (error) {
      console.error('Failed to import leads:', error);
      alert('Failed to import leads: ' + (error.response?.data?.error || error.message));
    } finally {
      setImporting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        alert('Please select a CSV file');
        return;
      }
      setCsvFile(file);
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      draft: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Draft' },
      active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
      paused: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Paused' },
      completed: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Completed' }
    };
    const config = configs[status] || configs.draft;
    return (
      <span className={clsx('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', config.bg, config.text)}>
        {config.label}
      </span>
    );
  };

  const renderCampaignsView = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Sales Campaigns</h2>
          <p className="text-sm text-gray-600 mt-1">Manage outbound sales campaigns and lead calling</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Campaign
        </button>
      </div>

      {/* Campaigns Grid */}
      {loading && campaigns.length === 0 ? (
        <LoadingSpinner />
      ) : campaigns.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Phone className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No campaigns yet</h3>
          <p className="text-gray-600 mb-6">Create your first sales campaign to start calling leads</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Campaign
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {campaigns.map(campaign => (
            <div
              key={campaign.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => {
                setSelectedCampaign(campaign);
                setActiveTab('leads');
              }}
            >
              {/* Campaign Header */}
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{campaign.name}</h3>
                  {getStatusBadge(campaign.status)}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditCampaign(campaign);
                    }}
                    className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                    title="Edit Campaign"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  {campaign.status === 'draft' || campaign.status === 'paused' ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartCampaign(campaign.id);
                      }}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                      title="Start Campaign"
                    >
                      <Play className="w-4 h-4" />
                    </button>
                  ) : campaign.status === 'active' ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePauseCampaign(campaign.id);
                      }}
                      className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg"
                      title="Pause Campaign"
                    >
                      <Pause className="w-4 h-4" />
                    </button>
                  ) : null}
                </div>
              </div>

              {/* Campaign Description */}
              {campaign.description && (
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{campaign.description}</p>
              )}

              {/* Campaign Metrics */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <div className="text-2xl font-bold text-gray-900">{campaign.totalLeads || 0}</div>
                  <div className="text-xs text-gray-500">Total Leads</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{campaign.contacted || 0}</div>
                  <div className="text-xs text-gray-500">Contacted</div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>Progress</span>
                  <span>{campaign.totalLeads > 0 ? Math.round((campaign.contacted / campaign.totalLeads) * 100) : 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${campaign.totalLeads > 0 ? (campaign.contacted / campaign.totalLeads) * 100 : 0}%` }}
                  />
                </div>
              </div>

              {/* Campaign Stats */}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-green-50 rounded p-2">
                  <div className="text-sm font-semibold text-green-700">{campaign.interested || 0}</div>
                  <div className="text-xs text-green-600">Interested</div>
                </div>
                <div className="bg-red-50 rounded p-2">
                  <div className="text-sm font-semibold text-red-700">{campaign.notInterested || 0}</div>
                  <div className="text-xs text-red-600">Not Int.</div>
                </div>
                <div className="bg-blue-50 rounded p-2">
                  <div className="text-sm font-semibold text-blue-700">{campaign.scheduled || 0}</div>
                  <div className="text-xs text-blue-600">Scheduled</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderLeadsView = () => {
    if (!selectedCampaign) {
      return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Select a campaign to view leads</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setSelectedCampaign(null);
                setActiveTab('campaigns');
              }}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              ← Back
            </button>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{selectedCampaign.name}</h2>
              <p className="text-sm text-gray-600">Lead Management</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Import from LeadGen
            </button>
            <button
              onClick={() => setShowAddLeadModal(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Lead
            </button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <select className="px-3 py-2 border border-gray-300 rounded-lg">
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="contacted">Contacted</option>
              <option value="interested">Interested</option>
              <option value="not_interested">Not Interested</option>
              <option value="scheduled">Scheduled</option>
            </select>
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-12"><LoadingSpinner /></div>
          ) : leads.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No leads yet</h3>
              <p className="text-gray-600 mb-6">Import a CSV file or add leads manually to get started</p>
              <button
                onClick={() => setShowImportModal(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import CSV
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attempts</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {leads.map(lead => (
                    <tr key={lead.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{lead.firstName} {lead.lastName}</div>
                        <div className="text-sm text-gray-500">{lead.email || '—'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{lead.company || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">{lead.phone}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(lead.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{lead.callAttempts || 0}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {lead.lastContactedAt ? new Date(lead.lastContactedAt).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center space-x-2">
                          <button className="p-1 hover:bg-gray-100 rounded" title="Call Now">
                            <PhoneCall className="w-4 h-4 text-blue-600" />
                          </button>
                          <button className="p-1 hover:bg-gray-100 rounded" title="View Details">
                            <Eye className="w-4 h-4 text-gray-600" />
                          </button>
                          <button className="p-1 hover:bg-gray-100 rounded" title="More">
                            <MoreVertical className="w-4 h-4 text-gray-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales Campaigns</h1>
          <p className="text-gray-600 mt-1">
            Manage outbound sales campaigns with lead tracking and call analytics
          </p>
        </div>
        <button
          onClick={loadCampaigns}
          className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('campaigns')}
              className={clsx(
                'py-4 px-1 border-b-2 font-medium text-sm',
                activeTab === 'campaigns'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
            >
              <Phone className="w-4 h-4 inline mr-2" />
              Campaigns
            </button>
            <button
              onClick={() => setActiveTab('leads')}
              className={clsx(
                'py-4 px-1 border-b-2 font-medium text-sm',
                activeTab === 'leads'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
              disabled={!selectedCampaign}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Leads {selectedCampaign && `(${selectedCampaign.name})`}
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={clsx(
                'py-4 px-1 border-b-2 font-medium text-sm',
                activeTab === 'analytics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Analytics
            </button>
            <button
              onClick={() => setActiveTab('categories')}
              className={clsx(
                'py-4 px-1 border-b-2 font-medium text-sm',
                activeTab === 'categories'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
            >
              <Tag className="w-4 h-4 inline mr-2" />
              Lead Categories
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'campaigns' && renderCampaignsView()}
          {activeTab === 'leads' && renderLeadsView()}
          {activeTab === 'analytics' && (
            <div className="text-center py-12 text-gray-500">
              Campaign analytics coming soon...
            </div>
          )}
          {activeTab === 'categories' && (
            <div className="space-y-6">
              {/* Categories Header */}
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Lead Categories</h3>
                  <p className="text-sm text-gray-600">Categorize leads with custom AI behavior for each category</p>
                </div>
                <button
                  onClick={() => {
                    resetCategoryForm();
                    setShowCategoryModal(true);
                  }}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Category
                </button>
              </div>

              {/* Categories Grid */}
              {categories.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-12 text-center">
                  <Tag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No categories yet</h3>
                  <p className="text-gray-600 mb-6">Create categories like "Hot Lead", "Cold Lead" to organize leads with custom AI behavior</p>
                  <button
                    onClick={() => {
                      resetCategoryForm();
                      setShowCategoryModal(true);
                    }}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Category
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categories.map(category => (
                    <div
                      key={category.id}
                      className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: category.color || '#6B7280' }}
                          />
                          <div>
                            <h4 className="font-medium text-gray-900">{category.name}</h4>
                            {category.description && (
                              <p className="text-sm text-gray-500">{category.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => handleEditCategory(category)}
                            className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteCategory(category.id)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">
                          {category._count?.leads || 0} leads
                        </span>
                        <span className={clsx(
                          'px-2 py-0.5 rounded-full text-xs',
                          category.useMainAiConfig
                            ? 'bg-gray-100 text-gray-600'
                            : 'bg-blue-100 text-blue-700'
                        )}>
                          {category.useMainAiConfig ? 'Main AI Config' : 'Custom AI'}
                        </span>
                      </div>

                      {!category.useMainAiConfig && category.aiTone && (
                        <div className="mt-2 text-xs text-gray-500">
                          Tone: {category.aiTone}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">
                {campaignForm.id ? 'Edit Sales Campaign' : 'Create Sales Campaign'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetCampaignForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateCampaign} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Basic Information</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name *</label>
                  <input
                    type="text"
                    value={campaignForm.name}
                    onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={campaignForm.description}
                    onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg h-20"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Objective</label>
                    <select
                      value={campaignForm.objective}
                      onChange={(e) => setCampaignForm({ ...campaignForm, objective: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="qualification">Lead Qualification</option>
                      <option value="demo_booking">Demo Booking</option>
                      <option value="product_sale">Product Sale</option>
                      <option value="feedback">Feedback Collection</option>
                    </select>
                  </div>
                </div>

                {/* Pipeline & Voice */}
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pipeline</label>
                    <select
                      value={campaignForm.pipeline}
                      onChange={(e) => handlePipelineChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      {PIPELINE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {PIPELINE_OPTIONS.find(p => p.value === campaignForm.pipeline)?.description}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Voice</label>
                    <select
                      value={campaignForm.voiceModel}
                      onChange={(e) => setCampaignForm({ ...campaignForm, voiceModel: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      {(VOICE_OPTIONS_BY_PIPELINE[campaignForm.pipeline] || []).map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Call Provider */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Call Provider</label>
                  <select
                    value={campaignForm.callProvider}
                    onChange={(e) => setCampaignForm({ ...campaignForm, callProvider: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="retell">Retell AI (Recommended)</option>
                    <option value="vonage">OpenAI Realtime (Legacy)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {campaignForm.callProvider === 'retell'
                      ? 'Uses Retell AI for natural conversation with built-in call handling'
                      : 'Uses OpenAI Realtime API with custom voice handling'}
                  </p>
                </div>
              </div>

              {/* Script Configuration */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Call Script</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Intro Message *</label>
                  <textarea
                    value={campaignForm.introMessage}
                    onChange={(e) => setCampaignForm({ ...campaignForm, introMessage: e.target.value })}
                    placeholder="Hey, this is your AI call assistant. I specialize in handling HVAC customer calls instantly..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg h-24"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Opening statement that introduces the value proposition</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Qualifying Question *</label>
                  <textarea
                    value={campaignForm.qualifyingQuestion}
                    onChange={(e) => setCampaignForm({ ...campaignForm, qualifyingQuestion: e.target.value })}
                    placeholder="Before I continue, are you the person who makes decisions about your company's customer calls and scheduling?"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg h-20"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Ensures you're speaking with the decision maker</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Question 1</label>
                  <input
                    type="text"
                    value={campaignForm.questions[0]}
                    onChange={(e) => {
                      const newQuestions = [...campaignForm.questions];
                      newQuestions[0] = e.target.value;
                      setCampaignForm({ ...campaignForm, questions: newQuestions });
                    }}
                    placeholder="How often are you missing calls right now — especially after hours or during busy days?"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1">Uncover the pain point and frequency</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Question 2</label>
                  <input
                    type="text"
                    value={campaignForm.questions[1]}
                    onChange={(e) => {
                      const newQuestions = [...campaignForm.questions];
                      newQuestions[1] = e.target.value;
                      setCampaignForm({ ...campaignForm, questions: newQuestions });
                    }}
                    placeholder="If I could handle those calls instantly, book the jobs, and filter out non-serious customers, would that help your team?"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1">Present the solution and gauge interest</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Closing Message *</label>
                  <textarea
                    value={campaignForm.closingMessage}
                    onChange={(e) => setCampaignForm({ ...campaignForm, closingMessage: e.target.value })}
                    placeholder="Thanks for your time. Someone from our team will reach out shortly to go over next steps and get everything finalized for you."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg h-20"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">End with clear next steps and expectations</p>
                </div>
              </div>

              {/* Calling Rules */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Calling Rules</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From Phone Number *</label>
                  <input
                    type="tel"
                    value={campaignForm.fromPhoneNumber}
                    onChange={(e) => setCampaignForm({ ...campaignForm, fromPhoneNumber: e.target.value })}
                    placeholder="+12183840817"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Attempts</label>
                    <input
                      type="number"
                      value={campaignForm.maxAttemptsPerLead}
                      onChange={(e) => setCampaignForm({ ...campaignForm, maxAttemptsPerLead: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      min="1"
                      max="10"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Retry Delay (hrs)</label>
                    <input
                      type="number"
                      value={campaignForm.retryDelayHours}
                      onChange={(e) => setCampaignForm({ ...campaignForm, retryDelayHours: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      min="1"
                      max="168"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                    <select
                      value={campaignForm.timezone}
                      onChange={(e) => setCampaignForm({ ...campaignForm, timezone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="America/Chicago">Central Time</option>
                      <option value="America/New_York">Eastern Time</option>
                      <option value="America/Denver">Mountain Time</option>
                      <option value="America/Los_Angeles">Pacific Time</option>
                    </select>
                  </div>
                </div>

                {/* Scheduling Mode Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Scheduling Mode</label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="schedulingMode"
                        value="window"
                        checked={campaignForm.schedulingMode === 'window'}
                        onChange={(e) => setCampaignForm({ ...campaignForm, schedulingMode: e.target.value })}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">Time Window</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="schedulingMode"
                        value="specific"
                        checked={campaignForm.schedulingMode === 'specific'}
                        onChange={(e) => setCampaignForm({ ...campaignForm, schedulingMode: e.target.value })}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">Specific Times</span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {campaignForm.schedulingMode === 'window'
                      ? 'Calls will be made continuously within the time window'
                      : 'Calls will only be made at the specified times each day'}
                  </p>
                </div>

                {/* Time Window Mode */}
                {campaignForm.schedulingMode === 'window' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Calling Hours Start</label>
                      <input
                        type="time"
                        value={campaignForm.callingHoursStart}
                        onChange={(e) => setCampaignForm({ ...campaignForm, callingHoursStart: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Calling Hours End</label>
                      <input
                        type="time"
                        value={campaignForm.callingHoursEnd}
                        onChange={(e) => setCampaignForm({ ...campaignForm, callingHoursEnd: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                )}

                {/* Specific Times Mode */}
                {campaignForm.schedulingMode === 'specific' && (
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Call Times (daily)
                    </label>
                    {campaignForm.specificCallTimes.map((time, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="time"
                          value={time}
                          onChange={(e) => updateCallTime(index, e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeCallTime(index)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          disabled={campaignForm.specificCallTimes.length === 1}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addCallTime}
                      className="flex items-center text-sm text-blue-600 hover:text-blue-700"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Another Time
                    </button>
                    <p className="text-xs text-gray-500">
                      Example: Set times like 10:30, 13:45, 15:50 to call at those specific times daily
                    </p>
                  </div>
                )}
              </div>

              {/* AI Configuration */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900">AI Configuration</h4>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={campaignForm.useMainAiConfig}
                      onChange={(e) => setCampaignForm({ ...campaignForm, useMainAiConfig: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-600">Use main AI settings</span>
                  </label>
                </div>

                {!campaignForm.useMainAiConfig && (
                  <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-700">
                      Configure custom AI behavior for this campaign. These settings will override the main AI configuration.
                    </p>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">AI Personality</label>
                      <textarea
                        value={campaignForm.aiPersonality}
                        onChange={(e) => setCampaignForm({ ...campaignForm, aiPersonality: e.target.value })}
                        placeholder="Example: You are a friendly sales representative for ABC Company, specializing in HVAC services. You're knowledgeable, helpful, and focused on understanding customer needs."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg h-24"
                      />
                      <p className="text-xs text-gray-500 mt-1">Define who the AI is and how it should present itself</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Conversation Tone</label>
                      <select
                        value={campaignForm.aiTone}
                        onChange={(e) => setCampaignForm({ ...campaignForm, aiTone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="friendly">Friendly - Warm and approachable</option>
                        <option value="professional">Professional - Polished and business-like</option>
                        <option value="urgent">Urgent - Enthusiastic with a sense of importance</option>
                        <option value="casual">Casual - Relaxed and conversational</option>
                        <option value="warm">Warm - Genuinely caring and personable</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Special Instructions</label>
                      <textarea
                        value={campaignForm.aiInstructions}
                        onChange={(e) => setCampaignForm({ ...campaignForm, aiInstructions: e.target.value })}
                        placeholder="Example: Always mention our current 20% off promotion. If they ask about pricing, emphasize value before discussing costs. Never pressure the customer."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg h-24"
                      />
                      <p className="text-xs text-gray-500 mt-1">Specific behaviors and guidelines for this campaign</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Business Context (Internal Only)</label>
                      <textarea
                        value={campaignForm.aiContext}
                        onChange={(e) => setCampaignForm({ ...campaignForm, aiContext: e.target.value })}
                        placeholder="Example: This campaign targets small business owners who requested HVAC quotes. They're price-sensitive but value quality service. Our main competitors are XYZ Corp."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg h-20"
                      />
                      <p className="text-xs text-gray-500 mt-1">Background information for the AI (will NOT be spoken aloud)</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {campaignForm.id ? 'Update Campaign' : 'Create Campaign'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import CSV Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Import Leads from CSV</h3>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setCsvFile(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleImportCSV} className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select CSV File
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-lg file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100"
                  required
                />
                {csvFile && (
                  <p className="mt-2 text-sm text-green-600">
                    Selected: {csvFile.name}
                  </p>
                )}
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">CSV Format Requirements:</h4>
                <p className="text-xs text-gray-600 mb-2">
                  Your CSV file must include these column headers (case-sensitive):
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="font-semibold text-red-600">Required:</span>
                    <ul className="list-disc list-inside ml-2 text-gray-700">
                      <li>firstName</li>
                      <li>lastName</li>
                      <li>phone</li>
                    </ul>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-600">Optional:</span>
                    <ul className="list-disc list-inside ml-2 text-gray-700">
                      <li>email</li>
                      <li>company</li>
                      <li>title</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowImportModal(false);
                    setCsvFile(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={importing}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                  disabled={importing || !csvFile}
                >
                  {importing ? 'Importing...' : 'Import Leads'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Lead Modal */}
      {showAddLeadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Add Lead</h3>
              <button
                onClick={() => setShowAddLeadModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddLead} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={leadForm.firstName}
                    onChange={(e) => setLeadForm({ ...leadForm, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={leadForm.lastName}
                    onChange={(e) => setLeadForm({ ...leadForm, lastName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={leadForm.phone}
                  onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                  placeholder="+12183840817"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={leadForm.email}
                  onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                  placeholder="john@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company
                </label>
                <input
                  type="text"
                  value={leadForm.company}
                  onChange={(e) => setLeadForm({ ...leadForm, company: e.target.value })}
                  placeholder="ACME Corp"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job Title
                </label>
                <input
                  type="text"
                  value={leadForm.title}
                  onChange={(e) => setLeadForm({ ...leadForm, title: e.target.value })}
                  placeholder="CEO"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowAddLeadModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add Lead
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lead Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">
                {categoryForm.id ? 'Edit Lead Category' : 'Create Lead Category'}
              </h3>
              <button
                onClick={() => {
                  setShowCategoryModal(false);
                  resetCategoryForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateCategory} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Category Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 sm:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category Name *</label>
                    <input
                      type="text"
                      value={categoryForm.name}
                      onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                      placeholder="e.g., Hot Lead, Warm Lead, Cold Lead"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={categoryForm.color}
                        onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                        className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={categoryForm.color}
                        onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="#6B7280"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={categoryForm.description}
                    onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                    placeholder="Brief description of this category..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg h-16"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <input
                    type="number"
                    value={categoryForm.priority}
                    onChange={(e) => setCategoryForm({ ...categoryForm, priority: parseInt(e.target.value) || 0 })}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg"
                    min="0"
                    max="100"
                  />
                  <p className="text-xs text-gray-500 mt-1">Higher priority categories are shown first (0-100)</p>
                </div>
              </div>

              {/* AI Configuration */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900">AI Configuration</h4>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={categoryForm.useMainAiConfig}
                      onChange={(e) => setCategoryForm({ ...categoryForm, useMainAiConfig: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-600">Use main AI settings</span>
                  </label>
                </div>

                {!categoryForm.useMainAiConfig && (
                  <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-700">
                      Configure custom AI behavior for leads in this category. These settings override both campaign and main AI settings.
                    </p>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">AI Personality</label>
                      <textarea
                        value={categoryForm.aiPersonality}
                        onChange={(e) => setCategoryForm({ ...categoryForm, aiPersonality: e.target.value })}
                        placeholder="Example: You are an enthusiastic sales rep focused on closing deals quickly. Be confident and persuasive."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg h-20"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Conversation Tone</label>
                      <select
                        value={categoryForm.aiTone}
                        onChange={(e) => setCategoryForm({ ...categoryForm, aiTone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="friendly">Friendly - Warm and approachable</option>
                        <option value="professional">Professional - Polished and business-like</option>
                        <option value="urgent">Urgent - Enthusiastic with a sense of importance</option>
                        <option value="casual">Casual - Relaxed and conversational</option>
                        <option value="warm">Warm - Genuinely caring and personable</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Special Instructions</label>
                      <textarea
                        value={categoryForm.aiInstructions}
                        onChange={(e) => setCategoryForm({ ...categoryForm, aiInstructions: e.target.value })}
                        placeholder="Example: These are hot leads ready to buy. Focus on closing. Address any final objections quickly."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg h-20"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Business Context (Internal)</label>
                      <textarea
                        value={categoryForm.aiContext}
                        onChange={(e) => setCategoryForm({ ...categoryForm, aiContext: e.target.value })}
                        placeholder="Example: These leads have shown strong buying signals. Average deal size is $5,000."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg h-16"
                      />
                      <p className="text-xs text-gray-500 mt-1">This context will NOT be spoken aloud</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowCategoryModal(false);
                    resetCategoryForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {categoryForm.id ? 'Update Category' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import External Leads Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Import Leads from External Source</h3>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportResult(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                &times;
              </button>
            </div>
            <div className="p-6 space-y-4">
              {!selectedCampaign ? (
                <div className="text-center py-4">
                  <p className="text-gray-600">Please select a campaign first to import leads.</p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                    <select
                      value={importSource}
                      onChange={(e) => setImportSource(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="leadgen">LeadGen (Vertical Platform)</option>
                      <option value="alfred_leadgen">Alfred LeadGen (Contractor Platform)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Select the external lead source to import from
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Stage Filter (optional)</label>
                    <select
                      value={importFilters.stage || ''}
                      onChange={(e) => setImportFilters({ ...importFilters, stage: e.target.value || undefined })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">All Stages</option>
                      <option value="QUALIFIED">Qualified</option>
                      <option value="CONTACTED">Contacted</option>
                      <option value="NEW">New</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Min Score (optional)</label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={importFilters.minScore || ''}
                      onChange={(e) => setImportFilters({ ...importFilters, minScore: e.target.value ? parseInt(e.target.value) : undefined })}
                      placeholder="e.g. 8"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  {importResult && (
                    <div className={`p-4 rounded-lg ${importResult.error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                      {importResult.error ? (
                        <p className="text-red-700">{importResult.error}</p>
                      ) : (
                        <div className="text-green-700">
                          <p className="font-medium">Import Complete!</p>
                          <p>Imported: {importResult.imported} leads</p>
                          {importResult.duplicates > 0 && (
                            <p className="text-sm">Skipped {importResult.duplicates} duplicates</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportResult(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                {importResult ? 'Close' : 'Cancel'}
              </button>
              {selectedCampaign && !importResult && (
                <button
                  onClick={handleImportLeads}
                  disabled={importLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {importLoading ? 'Importing...' : 'Import Leads'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesCampaignsPage;
